/* =========================================================
   Custom product grid
   Loads the chosen collection's products in the background and
   filters, sorts and paginates them inside this section only.
   Nothing outside <cpg-grid> is changed.
   ========================================================= */
(function () {
  'use strict';
  if (!window.customElements || customElements.get('cpg-grid')) return;

  var PAGE_SIZE = 250;
  var MAX_PAGES = 8;            // up to 2000 products per collection
  var CACHE_MS = 5 * 60 * 1000; // 5 minutes
  var DESKTOP = '(min-width: 990px)';
  var memoryCache = {};
  var productCache = {};

  /* ---------------- helpers ---------------- */
  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function toCents(v) {
    if (v == null || v === '') return 0;
    if (typeof v === 'number') return Math.round(v);
    return Math.round(parseFloat(String(v)) * 100);
  }

  function formatMoney(cents, format) {
    format = String(format || '{{amount}}').replace(/<[^>]*>/g, '');
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function fmt(number, precision, thousands, decimal) {
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      var n = (number / 100).toFixed(precision);
      var parts = n.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    var m = format.match(placeholder);
    if (!m) return (cents / 100).toFixed(2);
    var v;
    switch (m[1]) {
      case 'amount_no_decimals': v = fmt(cents, 0); break;
      case 'amount_with_comma_separator': v = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': v = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': v = fmt(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': v = fmt(cents, 0, ' '); break;
      case 'amount_with_space_separator': v = fmt(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': v = fmt(cents, 2, ' ', '.'); break;
      default: v = fmt(cents, 2);
    }
    return format.replace(placeholder, v);
  }

  function imgUrl(src, width) {
    if (!src) return '';
    var s = String(src);
    if (s.indexOf('//') === 0) s = 'https:' + s;
    return s + (s.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function srcset(src) {
    return [360, 540, 720, 900, 1080].map(function (w) { return imgUrl(src, w) + ' ' + w + 'w'; }).join(', ');
  }

  function names(str) {
    return String(str || '').split(',').map(function (x) { return x.trim().toLowerCase(); }).filter(Boolean);
  }

  function uniqSorted(arr) {
    var seen = {};
    var out = [];
    arr.forEach(function (x) { if (x && !seen[x]) { seen[x] = true; out.push(x); } });
    return out.sort(function (a, b) { return a.localeCompare(b, undefined, { numeric: true }); });
  }

  var SIZE_ORDER = ['xxxs', 'xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', '2xl', 'xxxl', '3xl', '4xl', '5xl', '6xl', '7xl'];
  function sortSizes(arr) {
    return arr.slice().sort(function (a, b) {
      var ia = SIZE_ORDER.indexOf(a.toLowerCase());
      var ib = SIZE_ORDER.indexOf(b.toLowerCase());
      if (ia > -1 && ib > -1) return ia - ib;
      if (ia > -1) return -1;
      if (ib > -1) return 1;
      return a.localeCompare(b, undefined, { numeric: true });
    });
  }

  function slim(p, index) {
    var variants = p.variants || [];
    var price = Infinity;
    var discount = 0;
    var compare = 0;
    variants.forEach(function (v) {
      var pr = toCents(v.price);
      var c = toCents(v.compare_at_price);
      if (pr < price) { price = pr; compare = c > pr ? c : 0; }
      if (c > pr) {
        var d = Math.round((c - pr) * 100 / c);
        if (d > discount) discount = d;
      }
    });
    if (price === Infinity) price = 0;
    var tags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',').map(function (t) { return t.trim(); }).filter(Boolean);
    return {
      i: index,
      handle: p.handle,
      title: p.title || '',
      type: p.product_type || '',
      vendor: p.vendor || '',
      tags: tags,
      available: variants.some(function (v) { return v.available; }),
      price: price,
      compare: compare,
      discount: discount,
      published: Date.parse(p.published_at || p.created_at || '') || 0,
      images: (p.images || []).slice(0, 2).map(function (im) { return { src: im.src, w: im.width, h: im.height }; }),
      options: (p.options || []).map(function (o) { return { name: String(o.name || ''), values: o.values || [] }; })
    };
  }

  function loadCollection(root, handle) {
    var key = 'cpg:' + handle;
    if (memoryCache[key]) return memoryCache[key];
    try {
      var cached = sessionStorage.getItem(key);
      if (cached) {
        var parsed = JSON.parse(cached);
        if (parsed && Date.now() - parsed.t < CACHE_MS) {
          memoryCache[key] = Promise.resolve(parsed.p);
          return memoryCache[key];
        }
      }
    } catch (e) { /* storage unavailable */ }

    memoryCache[key] = (async function () {
      var all = [];
      for (var page = 1; page <= MAX_PAGES; page++) {
        var url = root + 'collections/' + encodeURIComponent(handle) + '/products.json?limit=' + PAGE_SIZE + '&page=' + page;
        var res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error('Could not load products');
        var json = await res.json();
        var list = json.products || [];
        all = all.concat(list);
        if (list.length < PAGE_SIZE) break;
      }
      var data = all.map(slim);
      try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), p: data })); } catch (e) { /* quota */ }
      return data;
    })();
    memoryCache[key].catch(function () { delete memoryCache[key]; });
    return memoryCache[key];
  }

  function loadProduct(root, handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('Product not found'); return r.json(); });
      productCache[handle].catch(function () { delete productCache[handle]; });
    }
    return productCache[handle];
  }

  function lockScroll(on) {
    document.documentElement.classList.toggle('cpg-lock', !!on);
  }

  /* ---------------- element ---------------- */
  class CpgGrid extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      var cfgEl = this.querySelector('[data-cpg-config]');
      try { this.cfg = JSON.parse(cfgEl ? cfgEl.textContent : '{}'); } catch (e) { this.cfg = {}; }
      var c = this.cfg;
      c.root = c.root || '/';
      if (c.root.slice(-1) !== '/') c.root += '/';
      c.labels = c.labels || {};
      c.filters = c.filters || {};
      c.sort = c.sort || { options: {} };
      c.badges = c.badges || [];

      this.grid = this.querySelector('[data-cpg-grid]');
      this.pager = this.querySelector('[data-cpg-pagination]');
      this.emptyEl = this.querySelector('[data-cpg-empty]');
      this.countEl = this.querySelector('[data-cpg-count]');
      this.sortEl = this.querySelector('[data-cpg-sort]');
      this.filterCountEl = this.querySelector('[data-cpg-filter-count]');

      // Move overlays to <body> so theme layouts can't clip them
      this.drawer = this.querySelector('[data-cpg-drawer]');
      this.modal = this.querySelector('[data-cpg-qv-modal]');
      var vars = this.getAttribute('style') || '';
      if (this.drawer) { this.drawer.setAttribute('style', vars); document.body.appendChild(this.drawer); }
      if (this.modal) { this.modal.setAttribute('style', vars); document.body.appendChild(this.modal); }
      this.filterBoxes = [this.querySelector('[data-cpg-filters="side"]')];
      if (this.drawer) this.filterBoxes.push(this.drawer.querySelector('[data-cpg-filters="drawer"]'));
      this.filterBoxes = this.filterBoxes.filter(Boolean);

      this.mq = window.matchMedia(DESKTOP);
      this.openGroups = {};
      this.products = null;
      this.collectionIndex = 0;
      this.resetState();

      this.bind();
      this.buildSort();

      if (!c.collections || !c.collections.length) {
        this.classList.add('cpg--no-data');
        return;
      }

      // Start loading when the section is close to the screen
      var self = this;
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(function (entries) {
          if (entries.some(function (e) { return e.isIntersecting; })) {
            self.io.disconnect();
            self.load();
          }
        }, { rootMargin: '600px 0px' });
        this.io.observe(this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.drawer && this.drawer.parentNode) this.drawer.parentNode.removeChild(this.drawer);
      if (this.modal && this.modal.parentNode) this.modal.parentNode.removeChild(this.modal);
      document.removeEventListener('keydown', this.onKey);
      lockScroll(false);
    }

    resetState() {
      this.state = {
        sort: (this.cfg.sort && this.cfg.sort.default) || 'featured',
        page: 1,
        avail: {},
        min: '',
        max: '',
        type: {},
        vendor: {},
        size: {},
        color: {},
        tag: {}
      };
    }

    /* ---------- events ---------- */
    bind() {
      var self = this;

      if (this.sortEl) {
        this.sortEl.addEventListener('change', function () {
          self.state.sort = self.sortEl.value;
          self.state.page = 1;
          self.render(true);
        });
      }

      this.addEventListener('click', function (e) {
        var tab = e.target.closest('[data-cpg-tab]');
        if (tab) { self.switchTab(parseInt(tab.getAttribute('data-cpg-tab'), 10)); return; }

        var openF = e.target.closest('[data-cpg-open-filters]');
        if (openF) { self.openDrawer(); return; }

        var qv = e.target.closest('[data-cpg-qv]');
        if (qv) { e.preventDefault(); self.openQuickView(qv.getAttribute('data-cpg-qv')); return; }

        var pg = e.target.closest('[data-cpg-page]');
        if (pg) {
          e.preventDefault();
          self.state.page = parseInt(pg.getAttribute('data-cpg-page'), 10) || 1;
          self.render(false);
          self.scrollToTop();
          return;
        }

        var more = e.target.closest('[data-cpg-more]');
        if (more) { self.state.page += 1; self.render(false); }
      });

      this.filterBoxes.forEach(function (box) { self.bindFilterBox(box); });

      if (this.drawer) {
        this.drawer.addEventListener('click', function (e) {
          if (e.target.closest('[data-cpg-close-filters]')) self.closeDrawer();
          if (e.target.closest('[data-cpg-clear]') && !e.target.closest('[data-cpg-filters]')) self.clearAll();
        });
      }

      if (this.modal) {
        this.modal.addEventListener('click', function (e) {
          if (e.target.closest('[data-cpg-qv-close]')) { self.closeQuickView(); return; }
          if (e.target.closest('[data-cpg-qv-prev]')) { self.showQvImage(self.qvIndex - 1); return; }
          if (e.target.closest('[data-cpg-qv-next]')) { self.showQvImage(self.qvIndex + 1); return; }
          var th = e.target.closest('[data-cpg-qv-thumb]');
          if (th) self.showQvImage(parseInt(th.getAttribute('data-cpg-qv-thumb'), 10));
        });
      }

      this.onKey = function (e) {
        if (e.key !== 'Escape') return;
        if (self.modal && !self.modal.hidden) self.closeQuickView();
        else if (self.drawer && !self.drawer.hidden) self.closeDrawer();
      };
      document.addEventListener('keydown', this.onKey);

      var onMq = function () { if (self.mq.matches && self.drawer && !self.drawer.hidden && self.cfg.filters.enabled && self.classList.contains('cpg--filters-sidebar')) self.closeDrawer(); };
      if (this.mq.addEventListener) this.mq.addEventListener('change', onMq);
      else if (this.mq.addListener) this.mq.addListener(onMq);
    }

    bindFilterBox(box) {
      var self = this;
      box.addEventListener('change', function (e) {
        var t = e.target;
        var group = t.getAttribute('data-group');
        if (!group) return;
        if (group === 'min' || group === 'max') {
          self.state[group] = t.value;
        } else {
          var val = t.value;
          if (t.checked) self.state[group][val] = true;
          else delete self.state[group][val];
        }
        self.state.page = 1;
        self.render(true);
      });
      box.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && e.target.matches('input[type="number"]')) {
          e.preventDefault();
          e.target.blur();
        }
      });
      box.addEventListener('click', function (e) {
        var rm = e.target.closest('[data-cpg-remove]');
        if (rm) {
          var parts = rm.getAttribute('data-cpg-remove').split('::');
          if (parts[0] === 'price') { self.state.min = ''; self.state.max = ''; }
          else delete self.state[parts[0]][parts.slice(1).join('::')];
          self.state.page = 1;
          self.render(true);
        }
        if (e.target.closest('[data-cpg-clear]')) self.clearAll();
      });
      box.addEventListener('toggle', function (e) {
        var d = e.target;
        if (d.matches && d.matches('details[data-key]')) self.openGroups[d.getAttribute('data-key')] = d.open;
      }, true);
    }

    clearAll() {
      var sort = this.state.sort;
      this.resetState();
      this.state.sort = sort;
      this.render(true);
    }

    switchTab(index) {
      if (index === this.collectionIndex) return;
      this.collectionIndex = index;
      this.querySelectorAll('[data-cpg-tab]').forEach(function (b) {
        var on = parseInt(b.getAttribute('data-cpg-tab'), 10) === index;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      var sort = this.state.sort;
      this.resetState();
      this.state.sort = sort;
      this.products = null;
      this.load();
    }

    async load() {
      var col = this.cfg.collections[this.collectionIndex];
      if (!col) return;
      this.classList.add('is-loading');
      try {
        var data = await loadCollection(this.cfg.root, col.handle);
        if (this.cfg.collections[this.collectionIndex] !== col) return;
        this.products = data;
        this.classList.add('is-ready');
        this.render(true);
      } catch (err) {
        this.classList.add('cpg--load-failed');
      } finally {
        this.classList.remove('is-loading');
      }
    }

    /* ---------- filtering & sorting ---------- */
    optionIndex(p, wanted) {
      for (var i = 0; i < p.options.length; i++) {
        if (wanted.indexOf(p.options[i].name.toLowerCase()) > -1) return i;
      }
      return -1;
    }

    optionValues(p, wanted) {
      var idx = this.optionIndex(p, wanted);
      return idx > -1 ? p.options[idx].values : [];
    }

    visibleTags(p) {
      var prefix = String(this.cfg.filters.tagPrefix || '').toLowerCase();
      if (!prefix) return p.tags;
      return p.tags.filter(function (t) { return t.toLowerCase().indexOf(prefix) === 0; });
    }

    tagLabel(tag) {
      var prefix = String(this.cfg.filters.tagPrefix || '');
      return prefix && tag.toLowerCase().indexOf(prefix.toLowerCase()) === 0 ? tag.slice(prefix.length) : tag;
    }

    matches(p) {
      var st = this.state;
      var f = this.cfg.filters;
      var sizeNames = names(f.sizeName);
      var colorNames = names(f.colorName);
      var keys;

      keys = Object.keys(st.avail);
      if (keys.length) {
        var ok = (st.avail.in && p.available) || (st.avail.out && !p.available);
        if (!ok) return false;
      }
      var min = st.min === '' ? null : Math.round(parseFloat(st.min) * 100);
      var max = st.max === '' ? null : Math.round(parseFloat(st.max) * 100);
      if (min != null && !isNaN(min) && p.price < min) return false;
      if (max != null && !isNaN(max) && p.price > max) return false;

      keys = Object.keys(st.type);
      if (keys.length && !st.type[p.type]) return false;
      keys = Object.keys(st.vendor);
      if (keys.length && !st.vendor[p.vendor]) return false;

      keys = Object.keys(st.size);
      if (keys.length && !this.optionValues(p, sizeNames).some(function (v) { return st.size[v]; })) return false;
      keys = Object.keys(st.color);
      if (keys.length && !this.optionValues(p, colorNames).some(function (v) { return st.color[v]; })) return false;
      keys = Object.keys(st.tag);
      if (keys.length && !p.tags.some(function (t) { return st.tag[t]; })) return false;

      return true;
    }

    sorted(list) {
      var out = list.slice();
      var by = this.state.sort;
      var cmpTitle = function (a, b) { return a.title.localeCompare(b.title, undefined, { numeric: true }); };
      switch (by) {
        case 'newest': out.sort(function (a, b) { return b.published - a.published || a.i - b.i; }); break;
        case 'price-asc': out.sort(function (a, b) { return a.price - b.price || a.i - b.i; }); break;
        case 'price-desc': out.sort(function (a, b) { return b.price - a.price || a.i - b.i; }); break;
        case 'title-asc': out.sort(cmpTitle); break;
        case 'title-desc': out.sort(function (a, b) { return cmpTitle(b, a); }); break;
        case 'discount-desc': out.sort(function (a, b) { return b.discount - a.discount || a.i - b.i; }); break;
        default: out.sort(function (a, b) { return a.i - b.i; });
      }
      return out;
    }

    activeCount() {
      var st = this.state;
      var n = Object.keys(st.avail).length + Object.keys(st.type).length + Object.keys(st.vendor).length +
        Object.keys(st.size).length + Object.keys(st.color).length + Object.keys(st.tag).length;
      if (st.min !== '' || st.max !== '') n += 1;
      return n;
    }

    /* ---------- rendering ---------- */
    buildSort() {
      if (!this.sortEl) return;
      var L = this.cfg.labels;
      var opts = this.cfg.sort.options || {};
      var list = [
        ['featured', L.sortFeatured],
        ['newest', L.sortNewest],
        ['price-asc', L.sortPriceAsc],
        ['price-desc', L.sortPriceDesc],
        ['title-asc', L.sortTitleAsc],
        ['title-desc', L.sortTitleDesc],
        ['discount-desc', L.sortDiscount]
      ];
      var current = this.state.sort;
      var html = list.filter(function (o) { return opts[o[0]] || o[0] === current; }).map(function (o) {
        return '<option value="' + o[0] + '"' + (o[0] === current ? ' selected' : '') + '>' + esc(o[1]) + '</option>';
      }).join('');
      this.sortEl.innerHTML = html;
    }

    render(filtersChanged) {
      if (!this.products) return;
      var self = this;
      var cfg = this.cfg;
      var filtered = this.sorted(this.products.filter(function (p) { return self.matches(p); }));
      var per = Math.max(1, cfg.perPage || 12);
      var pages = Math.max(1, Math.ceil(filtered.length / per));
      if (this.state.page > pages) this.state.page = pages;

      var start = cfg.pagination === 'load_more' ? 0 : (this.state.page - 1) * per;
      var end = this.state.page * per;
      var slice = filtered.slice(start, end);

      this.grid.innerHTML = slice.map(function (p) { return self.cardHtml(p); }).join('');
      if (this.emptyEl) this.emptyEl.hidden = filtered.length > 0;
      if (this.countEl) this.countEl.textContent = String(cfg.labels.results || '[count] products').replace('[count]', filtered.length);

      this.renderPager(pages, filtered.length);
      if (filtersChanged) this.renderFilters();
      this.updateFilterCount();
    }

    renderPager(pages, total) {
      if (!this.pager) return;
      var st = this.state;
      var L = this.cfg.labels;
      if (this.cfg.pagination === 'load_more') {
        this.pager.innerHTML = st.page < pages
          ? '<button type="button" class="cpg-btn cpg-btn--ghost cpg__more" data-cpg-more>' + esc(L.loadMore) + '</button>'
          : '';
        return;
      }
      if (pages <= 1) { this.pager.innerHTML = ''; return; }
      var cur = st.page;
      var items = [];
      var add = function (n) { items.push(n); };
      add(1);
      for (var n = Math.max(2, cur - 1); n <= Math.min(pages - 1, cur + 1); n++) add(n);
      add(pages);
      items = items.filter(function (v, i, a) { return a.indexOf(v) === i; }).sort(function (a, b) { return a - b; });

      var html = '<ul class="cpg-pages" role="list">';
      html += '<li><a href="#" class="cpg-page cpg-page--arrow' + (cur === 1 ? ' is-disabled' : '') + '" data-cpg-page="' + Math.max(1, cur - 1) + '" aria-label="Previous page"' + (cur === 1 ? ' aria-disabled="true" tabindex="-1"' : '') + '>&lsaquo;</a></li>';
      var prev = 0;
      items.forEach(function (p) {
        if (p - prev > 1) html += '<li><span class="cpg-page cpg-page--gap">&hellip;</span></li>';
        html += '<li><a href="#" class="cpg-page' + (p === cur ? ' is-current' : '') + '" data-cpg-page="' + p + '"' + (p === cur ? ' aria-current="page"' : '') + '>' + p + '</a></li>';
        prev = p;
      });
      html += '<li><a href="#" class="cpg-page cpg-page--arrow' + (cur === pages ? ' is-disabled' : '') + '" data-cpg-page="' + Math.min(pages, cur + 1) + '" aria-label="Next page"' + (cur === pages ? ' aria-disabled="true" tabindex="-1"' : '') + '>&rsaquo;</a></li>';
      html += '</ul>';
      this.pager.innerHTML = html;
    }

    badgeHtml(p) {
      var cfg = this.cfg;
      var now = Date.now();
      var made = [];
      cfg.badges.forEach(function (b, i) {
        if (!b || !b.on || !b.text) return;
        var show = false;
        switch (b.rule) {
          case 'all': show = true; break;
          case 'sale': show = p.compare > p.price || p.discount > 0; break;
          case 'new': show = p.published && (now - p.published) <= (b.days || 30) * 86400000; break;
          case 'tag': show = !!b.tag && p.tags.some(function (t) { return t.toLowerCase() === String(b.tag).toLowerCase(); }); break;
          case 'sold_out': show = !p.available; break;
          case 'in_stock': show = p.available; break;
        }
        if (!show) return;
        var text = String(b.text).replace('[discount]', (p.discount || 0) + '%');
        made.push({ n: i + 1, pos: b.pos || 'tl', html: '<span class="cpg-badge cpg-badge--' + (i + 1) + '">' + esc(text) + '</span>' });
      });
      var groups = {};
      made.forEach(function (m) { (groups[m.pos] = groups[m.pos] || []).push(m.html); });
      return Object.keys(groups).map(function (pos) {
        return '<div class="cpg-badges cpg-badges--' + pos + '">' + groups[pos].join('') + '</div>';
      }).join('');
    }

    cardHtml(p) {
      var cfg = this.cfg;
      var L = cfg.labels;
      var url = cfg.root + 'products/' + encodeURIComponent(p.handle);
      var img1 = p.images[0];
      var img2 = p.images[1];
      var sizes = '(min-width: 990px) ' + Math.round(80 / (cfg.columnsDesktop || 4)) + 'vw, ' + Math.round(100 / (cfg.columnsMobile || 2)) + 'vw';
      var style = '';
      if (cfg.imageRatio === 'adapt' && img1 && img1.w && img1.h) style = ' style="--cpg-ratio: ' + img1.w + '/' + img1.h + ';"';

      var media = '';
      if (img1) {
        media += '<img class="cpg-card__img" src="' + esc(imgUrl(img1.src, 720)) + '" srcset="' + esc(srcset(img1.src)) + '" sizes="' + sizes + '" alt="' + esc(p.title) + '" loading="lazy"' + (img1.w ? ' width="' + img1.w + '" height="' + img1.h + '"' : '') + '>';
        if (cfg.hoverImage && img2) {
          media += '<img class="cpg-card__img cpg-card__img--hover" src="' + esc(imgUrl(img2.src, 720)) + '" srcset="' + esc(srcset(img2.src)) + '" sizes="' + sizes + '" alt="" loading="lazy">';
        }
      } else {
        media += '<span class="cpg-card__img cpg-card__noimg"></span>';
      }

      var price;
      if (p.compare > p.price) {
        price = '<span class="cpg-price cpg-price--sale">' + esc(formatMoney(p.price, cfg.moneyFormat)) + '</span>' +
          (cfg.showCompare ? '<s class="cpg-price cpg-price--compare">' + esc(formatMoney(p.compare, cfg.moneyFormat)) + '</s>' : '');
      } else {
        price = '<span class="cpg-price">' + esc(formatMoney(p.price, cfg.moneyFormat)) + '</span>';
      }

      var qvClass = 'cpg-qv-btn cpg-qv-btn--' + (cfg.qvPosition || 'bar');

      return '<li class="cpg-card" data-handle="' + esc(p.handle) + '"' + style + '>' +
        '<div class="cpg-card__media">' +
          '<a href="' + esc(url) + '" class="cpg-card__img-link" tabindex="-1" aria-hidden="true">' + media + '</a>' +
          this.badgeHtml(p) +
          (cfg.quickView ? '<button type="button" class="' + qvClass + '" data-cpg-qv="' + esc(p.handle) + '">' + esc(L.quickView) + '</button>' : '') +
        '</div>' +
        '<div class="cpg-card__info">' +
          (cfg.showVendor && p.vendor ? '<p class="cpg-card__vendor">' + esc(p.vendor) + '</p>' : '') +
          '<a href="' + esc(url) + '" class="cpg-card__title">' + esc(p.title) + '</a>' +
          '<div class="cpg-card__price">' + price + '</div>' +
        '</div>' +
      '</li>';
    }

    groupHtml(key, title, body, prefix) {
      var open = key in this.openGroups ? this.openGroups[key] : !!this.cfg.filters.openByDefault;
      return '<details class="cpg-f" data-key="' + key + '"' + (open ? ' open' : '') + '>' +
        '<summary class="cpg-f__head"><span>' + esc(title) + '</span>' +
        '<svg class="cpg-f__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></summary>' +
        '<div class="cpg-f__body">' + body + '</div></details>';
    }

    checkList(group, values, counts, prefix, labelFn) {
      var st = this.state[group];
      return '<ul class="cpg-f__list" role="list">' + values.map(function (v, i) {
        var id = prefix + '-' + group + '-' + i;
        var label = labelFn ? labelFn(v) : v;
        return '<li><label class="cpg-check" for="' + id + '">' +
          '<input type="checkbox" id="' + id + '" data-group="' + group + '" value="' + esc(v) + '"' + (st[v] ? ' checked' : '') + '>' +
          '<span class="cpg-check__box" aria-hidden="true"></span>' +
          '<span class="cpg-check__label">' + esc(label) + '</span>' +
          (counts && counts[v] != null ? '<span class="cpg-check__count">(' + counts[v] + ')</span>' : '') +
          '</label></li>';
      }).join('') + '</ul>';
    }

    renderFilters() {
      if (!this.filterBoxes.length || !this.cfg.filters.enabled || !this.products) return;
      var self = this;
      var f = this.cfg.filters;
      var L = this.cfg.labels;
      var products = this.products;
      var sizeNames = names(f.sizeName);
      var colorNames = names(f.colorName);

      var count = function (getter) {
        var map = {};
        products.forEach(function (p) {
          var vals = getter(p);
          (Array.isArray(vals) ? vals : [vals]).forEach(function (v) { if (v) map[v] = (map[v] || 0) + 1; });
        });
        return map;
      };

      this.filterBoxes.forEach(function (box, bi) {
        var prefix = 'cpg' + (self.getAttribute('data-section-id') || '') + '-' + bi;
        var html = '';

        // Active filters
        var chips = [];
        var st = self.state;
        ['avail', 'type', 'vendor', 'size', 'color', 'tag'].forEach(function (g) {
          Object.keys(st[g]).forEach(function (v) {
            var text = g === 'avail' ? (v === 'in' ? L.inStock : L.outOfStock) : (g === 'tag' ? self.tagLabel(v) : v);
            chips.push('<button type="button" class="cpg-chip" data-cpg-remove="' + g + '::' + esc(v) + '">' + esc(text) + ' <span aria-hidden="true">&times;</span></button>');
          });
        });
        if (st.min !== '' || st.max !== '') {
          chips.push('<button type="button" class="cpg-chip" data-cpg-remove="price">' + esc(L.price) + ': ' + esc(st.min || '0') + ' – ' + esc(st.max || '∞') + ' <span aria-hidden="true">&times;</span></button>');
        }
        if (chips.length) {
          html += '<div class="cpg-active">' + chips.join('') + '<button type="button" class="cpg-clear" data-cpg-clear>' + esc(L.clear) + '</button></div>';
        }

        if (f.availability) {
          var inCount = products.filter(function (p) { return p.available; }).length;
          var outCount = products.length - inCount;
          var body = '<ul class="cpg-f__list" role="list">' +
            [['in', L.inStock, inCount], ['out', L.outOfStock, outCount]].map(function (o) {
              var id = prefix + '-avail-' + o[0];
              return '<li><label class="cpg-check" for="' + id + '"><input type="checkbox" id="' + id + '" data-group="avail" value="' + o[0] + '"' + (st.avail[o[0]] ? ' checked' : '') + '><span class="cpg-check__box" aria-hidden="true"></span><span class="cpg-check__label">' + esc(o[1]) + '</span><span class="cpg-check__count">(' + o[2] + ')</span></label></li>';
            }).join('') + '</ul>';
          html += self.groupHtml('avail', L.availability, body);
        }

        if (f.price) {
          var prices = products.map(function (p) { return p.price; });
          var hi = prices.length ? Math.ceil(Math.max.apply(null, prices) / 100) : 0;
          var pbody = '<div class="cpg-price-range">' +
            '<label><span class="cpg-vh">Minimum</span><input type="number" inputmode="numeric" min="0" max="' + hi + '" placeholder="0" data-group="min" value="' + esc(st.min) + '"></label>' +
            '<span class="cpg-price-range__sep">–</span>' +
            '<label><span class="cpg-vh">Maximum</span><input type="number" inputmode="numeric" min="0" max="' + hi + '" placeholder="' + hi + '" data-group="max" value="' + esc(st.max) + '"></label>' +
            '</div>';
          html += self.groupHtml('price', L.price, pbody);
        }

        if (f.size) {
          var sizeCounts = count(function (p) { return self.optionValues(p, sizeNames); });
          var sizes = sortSizes(Object.keys(sizeCounts));
          if (sizes.length) html += self.groupHtml('size', L.size, self.checkList('size', sizes, sizeCounts, prefix));
        }

        if (f.color) {
          var colorCounts = count(function (p) { return self.optionValues(p, colorNames); });
          var colors = uniqSorted(Object.keys(colorCounts));
          if (colors.length) html += self.groupHtml('color', L.color, self.checkList('color', colors, colorCounts, prefix));
        }

        if (f.type) {
          var typeCounts = count(function (p) { return p.type; });
          var types = uniqSorted(Object.keys(typeCounts));
          if (types.length) html += self.groupHtml('type', L.type, self.checkList('type', types, typeCounts, prefix));
        }

        if (f.vendor) {
          var vendorCounts = count(function (p) { return p.vendor; });
          var vendors = uniqSorted(Object.keys(vendorCounts));
          if (vendors.length) html += self.groupHtml('vendor', L.vendor, self.checkList('vendor', vendors, vendorCounts, prefix));
        }

        if (f.tags) {
          var tagCounts = count(function (p) { return self.visibleTags(p); });
          var tags = uniqSorted(Object.keys(tagCounts));
          if (tags.length) html += self.groupHtml('tag', L.tags, self.checkList('tag', tags, tagCounts, prefix, function (t) { return self.tagLabel(t); }));
        }

        box.innerHTML = html;
      });
    }

    updateFilterCount() {
      if (!this.filterCountEl) return;
      var n = this.activeCount();
      this.filterCountEl.hidden = n === 0;
      this.filterCountEl.textContent = n ? String(n) : '';
    }

    scrollToTop() {
      var top = this.getBoundingClientRect().top + window.pageYOffset - (this.cfg.scrollOffset || 0);
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    /* ---------- drawer ---------- */
    openDrawer() {
      if (!this.drawer) return;
      this.lastFocus = document.activeElement;
      this.drawer.hidden = false;
      lockScroll(true);
      var panel = this.drawer.querySelector('.cpg-drawer__panel');
      requestAnimationFrame(function () {
        this.drawer.classList.add('is-open');
        if (panel) panel.focus();
      }.bind(this));
    }

    closeDrawer() {
      if (!this.drawer || this.drawer.hidden) return;
      var d = this.drawer;
      d.classList.remove('is-open');
      lockScroll(false);
      setTimeout(function () { d.hidden = true; }, 250);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
      if (this.products) this.scrollToTop();
    }

    /* ---------- quick view ---------- */
    async openQuickView(handle) {
      if (!this.modal || !handle) return;
      var m = this.modal;
      this.lastFocus = document.activeElement;
      m.hidden = false;
      m.classList.add('is-loading');
      lockScroll(true);
      requestAnimationFrame(function () { m.classList.add('is-open'); });
      var dialog = m.querySelector('.cpg-qv__dialog');
      if (dialog) dialog.focus();

      try {
        var p = await loadProduct(this.cfg.root, handle);
        this.fillQuickView(p);
      } catch (e) {
        var title = m.querySelector('[data-cpg-qv-title]');
        if (title) title.textContent = 'Sorry, this product could not be loaded.';
      } finally {
        m.classList.remove('is-loading');
      }
    }

    fillQuickView(p) {
      var m = this.modal;
      var cfg = this.cfg;
      var L = cfg.labels;
      var images = (p.images || []).map(function (src) { return typeof src === 'string' ? src : (src && src.src); }).filter(Boolean);
      if (!images.length && p.featured_image) images = [p.featured_image];
      this.qvImages = images;
      this.qvTitle = p.title || '';

      m.querySelector('[data-cpg-qv-title]').textContent = p.title || '';
      var vendorEl = m.querySelector('[data-cpg-qv-vendor]');
      if (vendorEl) vendorEl.textContent = cfg.showVendor ? (p.vendor || '') : '';

      var price = p.price;
      var compare = p.compare_at_price;
      var priceHtml = '';
      if (compare && compare > price) {
        priceHtml = '<span class="cpg-price cpg-price--sale">' + esc(formatMoney(price, cfg.moneyFormat)) + '</span>' +
          '<s class="cpg-price cpg-price--compare">' + esc(formatMoney(compare, cfg.moneyFormat)) + '</s>';
      } else {
        priceHtml = '<span class="cpg-price">' + esc(formatMoney(price, cfg.moneyFormat)) + '</span>';
      }
      if (!p.available) priceHtml += '<span class="cpg-qv__soldout">' + esc(L.soldOut) + '</span>';
      m.querySelector('[data-cpg-qv-price]').innerHTML = priceHtml;

      // Sizes with availability
      var sizeNames = names(cfg.filters.sizeName);
      var sizesEl = m.querySelector('[data-cpg-qv-sizes]');
      var sizeHtml = '';
      var opts = p.options || [];
      for (var i = 0; i < opts.length; i++) {
        var oname = typeof opts[i] === 'string' ? opts[i] : opts[i].name;
        if (sizeNames.indexOf(String(oname).toLowerCase()) === -1) continue;
        var values = (typeof opts[i] === 'object' && opts[i].values) ? opts[i].values : [];
        var key = 'option' + (i + 1);
        sizeHtml = '<p class="cpg-qv__label">' + esc(oname) + '</p><ul class="cpg-qv__size-list" role="list">' +
          values.map(function (v) {
            var avail = (p.variants || []).some(function (vr) { return vr[key] === v && vr.available; });
            return '<li class="cpg-qv__size' + (avail ? '' : ' is-soldout') + '">' + esc(v) + '</li>';
          }).join('') + '</ul>';
        break;
      }
      if (sizesEl) sizesEl.innerHTML = sizeHtml;

      var descEl = m.querySelector('[data-cpg-qv-desc]');
      if (descEl) {
        var tmp = document.createElement('div');
        tmp.innerHTML = p.description || '';
        var text = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
        descEl.textContent = text.length > 260 ? text.slice(0, 257).trim() + '…' : text;
      }

      var link = m.querySelector('[data-cpg-qv-link]');
      if (link) link.href = p.url || (cfg.root + 'products/' + encodeURIComponent(p.handle));

      var thumbs = m.querySelector('[data-cpg-qv-thumbs]');
      if (thumbs) {
        thumbs.innerHTML = images.length > 1 ? images.map(function (src, i) {
          return '<button type="button" class="cpg-qv__thumb" data-cpg-qv-thumb="' + i + '" aria-label="Image ' + (i + 1) + '"><img src="' + esc(imgUrl(src, 160)) + '" alt="" loading="lazy"></button>';
        }).join('') : '';
      }
      m.classList.toggle('cpg-qv--single', images.length < 2);
      this.showQvImage(0);
    }

    showQvImage(i) {
      var imgs = this.qvImages || [];
      if (!imgs.length) return;
      if (i < 0) i = imgs.length - 1;
      if (i >= imgs.length) i = 0;
      this.qvIndex = i;
      var img = this.modal.querySelector('[data-cpg-qv-img]');
      img.src = imgUrl(imgs[i], 1200);
      img.srcset = [600, 900, 1200, 1600].map(function (w) { return imgUrl(imgs[i], w) + ' ' + w + 'w'; }).join(', ');
      img.sizes = '(min-width: 750px) 50vw, 100vw';
      img.alt = this.qvTitle + ' – image ' + (i + 1);
      this.modal.querySelectorAll('[data-cpg-qv-thumb]').forEach(function (t) {
        t.classList.toggle('is-active', parseInt(t.getAttribute('data-cpg-qv-thumb'), 10) === i);
      });
    }

    closeQuickView() {
      if (!this.modal || this.modal.hidden) return;
      var m = this.modal;
      m.classList.remove('is-open');
      lockScroll(false);
      setTimeout(function () { m.hidden = true; }, 250);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }
  }

  customElements.define('cpg-grid', CpgGrid);
})();
